import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
const auth = localStorage.getItem("auth");
const user = JSON.parse(auth);

const baseUrl =
  process.env.NODE_ENV === "development"
    ? process.env.REACT_APP_DEV_URL
    : process.env.REACT_APP_PROD_URL;

//FILE API

export const fileApi = createApi({
  tagTypes: ["fileUpload"],
  reducerPath: "fileApi",
  baseQuery: fetchBaseQuery({
    baseUrl: `${baseUrl}/`,
    prepareHeaders: (headers, { getState }) => {
      const token = getState()?.authState?.userToken || user?.token;
      if (token) headers.set("Authorization", token);
      return headers;
    },
  }),
  endpoints: (builder) => ({
    fileUpload: builder.mutation({
      query: (payload) => ({
        url: `uploads?type=${payload?.type}`,
        method: "POST",
        body: payload?.file,
      }),
      invalidatesTags: ["fileUpload"],
    }),
    LogofileUpload: builder.mutation({
      query: ({ createdBy, file, imagetype, type, fileId }) => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("type", type);
        formData.append("image_type", imagetype);
        formData.append("createdBy", createdBy);
        formData.append("fileId", fileId);
        return {
          url: `uploads/logoupload?type=${type}`,
          method: "POST",
          body: formData,
        };
      },
    }),
    sendInvoiceEmail: builder.mutation({
      query: (payload) => ({
        url: `uploads/send-invoice-email`,
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: payload,
      }),
    }),
    getEmails: builder.query({
      query: () => ({
        url: `uploads/emails`, // This should map to your controller `getEmailData`
        method: "GET",
      }),
    }),
    getContractorFileUploads: builder.query({
      query: (contractorId) => ({
        url: `/uploads/contractor/${contractorId}`,
        method: "GET",
      }),
      providesTags: ["fileUpload"],
    }),
    getXlsxFilesUploads: builder.query({
      query: (userId) => ({
        url: `/uploads/files/xlsx?userId=${userId}`,
        method: "GET",
      }),
    }),
    downloadXlsxRows: builder.mutation({
      query: ({ fileId, start, end }) => ({
        url: `/uploads/files/xlsx/download/${fileId}?start=${start}&end=${end}`,
        method: "GET",
        responseHandler: async (response) => {
          // Convert response to blob for downloading
          const blob = await response.blob();
          return blob;
        },
      }),
    }),
    deleteFile: builder.mutation({
      query: (fileId) => ({
        url: `/uploads/${fileId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["fileUpload"],
    }),
    reviewContractorFile: builder.mutation({
      query: (payload) => ({
        url: `/uploads/review`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["fileUpload"],
    }),
  }),
});
export const {
  useFileUploadMutation,
  useLogofileUploadMutation,
  useSendInvoiceEmailMutation,
  useGetEmailsQuery,
  useGetContractorFileUploadsQuery,
  useGetXlsxFilesUploadsQuery,
  useDownloadXlsxRowsMutation,
  useDeleteFileMutation,
  useReviewContractorFileMutation
} = fileApi;

// EMPLOYEE API

export const employeeApi = createApi({
  reducerPath: "employeeApi",
  tagTypes: ["employeeroutes"],
  baseQuery: fetchBaseQuery({
    baseUrl: `${baseUrl}/`,
    prepareHeaders: (headers, { getState }) => {
      const token = getState()?.authState?.userToken;
      if (token) {
        headers.set("Authorization", token);
      }
      return headers;
    },
  }),
  endpoints: (builder) => ({
    createEmployee: builder.mutation({
      query: (payload) => ({
        url: "employeeroutes",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["employeeroutes"],
    }),
    addEmployeeSalary: builder.mutation({
      query: ({ employeeId, salaryData }) => ({
        url: `employeeroutes/${employeeId}/salary`,
        method: 'PUT',
        body: salaryData,
      }),
      invalidatesTags: ["employeeroutes"],
    }),
    updateEmployeeSalary: builder.mutation({
      query: (payload) => ({
        url: "employeeroutes/update",
        method: "POST",   // Matches your Express route: router.post("/update", ...)
        body: payload,
      }),
      invalidatesTags: ["employeeroutes"],
    }),
    getEmployeeList: builder.query({
      query: (createdById) => ({
        url: `employeeroutes/list?createdBy=${createdById}`,
        method: "GET",
      }),
      providesTags: ["employeeroutes"],
    }),
    employeeById: builder.query({
      query: (id) => ({
        url: `/employeeroutes/byid/${id}`,
        method: "GET",
      }),
      providesTags: ["employee"],
    }),
    deleteEmployee: builder.mutation({
      query: (id) => ({
        url: `employeeroutes/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["employee"],
    }),
    deleteEmployeeSalary: builder.mutation({
      query: ({ employeeId, salaryDetailsItemId }) => ({
        url: `/employeeroutes/${employeeId}/salaries/${salaryDetailsItemId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["employeeroutes"], // Adjust tag type as needed
    }),
    getMyEmployeeProfile: builder.query({
      query: () => ({
        url: "employeeroutes/me",
        method: "GET",
      }),
      providesTags: ["employeeroutes"],
    }),
    verifyEmergencyContact: builder.mutation({
      query: ({ id, decision }) => ({
        url: `employeeroutes/${id}/emergency-verify`,
        method: "PUT",
        body: { decision },
      }),
      invalidatesTags: ["employeeroutes"],
    }),
    reviseEmployeeSalary: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `employeeroutes/${id}/salary-revision`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["employeeroutes"],
    }),
    getSalaryRevisions: builder.query({
      query: (id) => ({
        url: `employeeroutes/${id}/salary-revisions`,
        method: "GET",
      }),
      providesTags: ["employeeroutes"],
    }),
    updateMyEmergencyContact: builder.mutation({
      query: (body) => ({
        url: "employeeroutes/me/emergency-contact",
        method: "PUT",
        body,
      }),
      invalidatesTags: ["employeeroutes"],
    }),
    updateMyLocation: builder.mutation({
      query: (body) => ({
        url: "employeeroutes/location",
        method: "PUT",
        body,
      }),
    }),
    updateEmployeefileid: builder.mutation({
      query: (body) => ({
        url: "employeeroutes/updated/",
        method: "POST",
        body,
      }),
      invalidatesTags: ["employeeroutes"],
    }),
    getEmployeeProfileById: builder.query({
      query: (id) => ({
        url: `/employeeroutes/byid/${id}`,
        method: "GET",
      }),
      providesTags: ["employee"],
    }),
  }),
});
export const {
  useCreateEmployeeMutation,
  useAddEmployeeSalaryMutation,
  useUpdateEmployeeSalaryMutation,
  useGetEmployeeListQuery,
  useEmployeeByIdQuery,
  useDeleteEmployeeSalaryMutation,
  useEmployeeDropdownQuery,
  useDeleteEmployeeMutation,
  useGetMyEmployeeProfileQuery,
  useVerifyEmergencyContactMutation,
  useReviseEmployeeSalaryMutation,
  useGetSalaryRevisionsQuery,
  useUpdateMyEmergencyContactMutation,
  useUpdateMyLocationMutation,
  useUpdateEmployeefileidMutation,
  useLazyGetEmployeeProfileByIdQuery,
} = employeeApi;

// STAFF API

export const staffApi = createApi({
  reducerPath: "staffApi",
  baseQuery: fetchBaseQuery({
    baseUrl: `${baseUrl}/`,
    prepareHeaders: (headers, { getState }) => {
      const token = getState()?.authState?.userToken;
      if (token) {
        headers.set("Authorization", token);
      }
      return headers;
    },
  }),
  tagTypes: ["staff"],
  endpoints: (builder) => ({
    getStaffList: builder.mutation({
      query: (payload) => ({
        url: "staff/list",
        method: "POST",
        body: payload,
      }),
      providesTags: ["staff"],
    }),
    getStaffCounts: builder.mutation({
      query: (payload) => ({
        url: "Staff/counts",
        method: "POST",
        body: payload,
      }),
    }),
    getClientCounts: builder.mutation({
      query: (payload) => ({
        url: "client/counts",
        method: "POST",
        body: payload,
      }),
    }),
    staffById: builder.query({
      query: (id) => ({
        url: `staff/byid/${id}`,
        method: "GET",
      }),
      providesTags: ["Staff"],
    }),
    staffDropdown: builder.query({
      query: () => ({
        url: "staff/dropdown",
        method: "GET",
      }),
      providesTags: ["staff"],
    }),
    deleteStaff: builder.mutation({
      query: (id) => ({
        url: `staff/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["staff"],
    }),
  }),
});

export const {
  useGetStaffListMutation,
  useGetStaffCountsMutation,
  useStaffByIdQuery,
  useStaffDropdownQuery,
  useDeleteStaffMutation,
} = staffApi;

// RTQ Query for autAadmin

export const authApi = createApi({
  tagTypes: ["auth"],
  reducerPath: "authApi",
  baseQuery: fetchBaseQuery({
    baseUrl: `${baseUrl}/`,
    prepareHeaders: (headers, { getState }) => {
      if (getState()?.authState?.userToken) {
        headers.set("Authorization", getState()?.authState?.userToken);
      }
      return headers;
    },
  }),
  endpoints: (builder) => ({
    createUser: builder.mutation({
      query: (payload) => ({
        url: "admin",
        method: "POST",
        body: payload,
      }),
      providesTags: ["auth"],
    }),
    userById: builder.query({
      query: (id) => ({
        url: `admin/byid/${id}`,
        method: "GET",
      }),
      providesTags: ["auth"],
    }),
    loginAuth: builder.mutation({
      query: (payload) => ({
        url: "admin/login",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["auth"],
    }),
    loginAsAdmin: builder.mutation({
      query: (payload) => ({
        url: "admin/staff/login/admin",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["auth"],
    }),
    userList: builder.mutation({
      query: (payload) => ({
        url: "admin/list",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["auth"],
    }),

    forgetPassword: builder.mutation({
      query: (payload) => ({
        url: "admin/forgot-password",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["auth"],
    }),
    resetPassword: builder.mutation({
      query: (payload) => ({
        url: "admin/reset-password",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["auth"],
    }),
    updateUser: builder.mutation({
      query: (payload) => ({
        url: "admin/updateUser",
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["auth"],
    }),
  }),
});

export const {
  useCreateUserMutation,
  useUserByIdQuery,
  useLoginAuthMutation,
  useLoginAsAdminMutation,
  useForgetPasswordMutation,
  useResetPasswordMutation,
  useUserListMutation,
  useUpdateUserMutation,
} = authApi;

//RTQ Query for client

export const clientApi = createApi({
  tagTypes: ["client"],
  reducerPath: "clientApi",
  baseQuery: fetchBaseQuery({
    baseUrl: `${baseUrl}/`,
    prepareHeaders: (headers, { getState }) => {
      if (getState()?.authState?.userToken) {
        headers.set("Authorization", getState()?.authState?.userToken);
      }
      return headers;
    },
  }),
  endpoints: (builder) => ({
    createClient: builder.mutation({
      query: (payload) => ({
        url: "client",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["client"],
    }),
    getClientCounts: builder.mutation({
      query: (payload) => ({
        url: "client/counts",
        method: "POST",
        body: payload,
      }),
    }),
    clientList: builder.mutation({
      query: (payload) => ({
        url: "client/list",
        method: "POST",
        body: payload,
      }),
      providesTags: ["client"],
    }),
    clientById: builder.query({
      query: (id) => ({
        url: `client/byid/${id}`,
        method: "GET",
      }),
      providesTags: ["client"],
    }),
    ClientdetialById: builder.query({
      query: (id) => ({
        url: `client/byInvoiceid/${id}`,
        method: "GET",
      }),
      providesTags: ["client"],
    }),
    clientByProjectId: builder.mutation({
      query: (id) => ({
        url: `client/byprojectid/${id}`,
        method: "POST",
      }),
      providesTags: ["client"],
    }),
    clientDropdown: builder.query({
      query: () => ({
        url: "client/dropdown",
        method: "GET",
      }),
      providesTags: ["client"],
    }),
    getTags: builder.query({
      query: (search = "") => ({
        url: `client/work-tags?q=${encodeURIComponent(search)}`,
        method: "GET",
      }),
      providesTags: ["client"],
    }),
    deleteClient: builder.mutation({
      query: (id) => ({
        url: `client/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["client"],
    }),
    getPendingClients: builder.query({
      query: (body) => ({
        url: "/client/pending",
        method: "POST",
        body,
      }),
      invalidatesTags: ["client"],
    }),
    getBillingOverview: builder.mutation({
      query: (body) => ({
        url: "client/billing-overview",
        method: "POST",
        body,
      }),
    }),
  }),
});

export const {
  useCreateClientMutation,
  useClientListMutation,
  useGetBillingOverviewMutation,
  useGetClientCountsMutation,
  useClientByIdQuery,
  useClientdetialByIdQuery,
  useClientByProjectIdMutation,
  useClientDropdownQuery,
  useGetTagsQuery,
  useDeleteClientMutation,
  useGetPendingClientsQuery,
} = clientApi;

//COMPANY PROFILE

export const companyProfileApi = createApi({
  tagTypes: ["companyProfile"],
  reducerPath: "companyProfileApi",
  baseQuery: fetchBaseQuery({
    baseUrl: `${baseUrl}/`,
    prepareHeaders: (headers, { getState }) => {
      if (getState()?.authState?.userToken) {
        headers.set("Authorization", getState()?.authState?.userToken);
      }
      return headers;
    },
  }),
  endpoints: (builder) => ({
    createEditProfile: builder.mutation({
      query: (formData) => ({
        url: "company/profile",
        method: "POST",
        body: formData,
      }),
      invalidatesTags: ["companyProfile"],
    }),
    getCompanyProfile: builder.query({
      query: (id) => ({
        url: `company/profile/${id}`,
        method: "GET",
      }),
      providesTags: ["companyProfile"],
    }),
  }),
});

export const { useCreateEditProfileMutation, useGetCompanyProfileQuery } =
  companyProfileApi;

//INVOICES API

export const invoiceDetailApi = createApi({
  tagTypes: ["invoiceDetail"],
  reducerPath: "invoiceDetailApi",
  baseQuery: fetchBaseQuery({
    baseUrl: `${baseUrl}/`,
    prepareHeaders: (headers, { getState }) => {
      if (getState()?.authState?.userToken) {
        headers.set("Authorization", getState()?.authState?.userToken);
      }
      return headers;
    },
  }),
  endpoints: (builder) => ({
    createInvoiceDetail: builder.mutation({
      query: (payload) => ({
        url: "invoice/create",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["invoiceDetail"],
    }),
    getInvoiceDetailById: builder.query({
      query: (id) => ({
        url: `invoice/detail/${id}`,
        method: "GET",
      }),
      providesTags: ["invoiceDetail"],
    }),

    updateInvoiceDetail: builder.mutation({
      query: (body) => ({
        url: "invoice/update/",
        method: "POST",
        body,
      }),
      invalidatesTags: ["invoiceDetail"],
    }),
    updateInvoicefileid: builder.mutation({
      query: (body) => ({
        url: "invoice/updated/",
        method: "POST",
        body,
      }),
      invalidatesTags: ["invoiceDetail"],
    }),
    deleteInvoiceDetail: builder.mutation({
      query: ({ invoiceId, detailId }) => ({
        url: `invoice/delete-detail/${invoiceId}/${detailId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["invoiceDetail"],
    }),

    NextInvoiceNumber: builder.query({
      query: () => ({
        url: "invoice/next-number",
        method: "GET",
      }),
      providesTags: ["invoiceDetail"],
    }),
    listInvoiceDetails: builder.query({
      query: () => ({
        url: "invoice/detail/list",
        method: "GET",
      }),
      providesTags: ["invoiceDetail"],
    }),
    MonthlyClientInvoices: builder.query({
      query: (id) => ({ url: `invoice/monthly-clients/${id}`, method: "GET" }),
      providesTags: ["invoiceDetail"],
    }),
    FxRates: builder.query({
      query: () => ({ url: "invoice/fx-rates", method: "GET" }),
    }),
    TotalRevenue: builder.query({
      query: (id) => ({
        url: `invoice/total/${id}`,
        method: "GET",
      }),
      providesTags: ["invoiceDetail"],
    }),
  }),
});

export const {
  useCreateInvoiceDetailMutation,
  useGetInvoiceDetailByIdQuery,
  useUpdateInvoiceDetailMutation,
  useUpdateInvoicefileidMutation,
  useDeleteInvoiceDetailMutation,
  useNextInvoiceNumberQuery,
  useListInvoiceDetailsQuery,
  useTotalRevenueQuery,
  useFxRatesQuery,
  useMonthlyClientInvoicesQuery,
} = invoiceDetailApi;

// CONTRACTOR API

export const contractorApi = createApi({
  reducerPath: "contractorApi",
  tagTypes: ["contractorroutes", "contractorFinance", "contractorHours", "contractorNotes"],
  baseQuery: fetchBaseQuery({
    baseUrl: `${baseUrl}/`,
    prepareHeaders: (headers, { getState }) => {
      const token = getState()?.authState?.userToken;
      if (token) {
        headers.set("Authorization", token);
      }
      return headers;
    },
  }),
  endpoints: (builder) => ({
    createContractor: builder.mutation({
      query: (payload) => ({
        url: "contractorroutes",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["contractorroutes"],
    }),
    getContractorFinance: builder.query({
      query: (pin) => ({
        url: "contractorroutes/finance-overview",
        method: "GET",
        headers: { "x-finance-pin": pin || "" },
      }),
      providesTags: ["contractorFinance"],
    }),
    setContractorBilling: builder.mutation({
      query: ({ id, amount, currency, pin }) => ({
        url: `contractorroutes/billing/${id}`,
        method: "PUT",
        headers: { "x-finance-pin": pin || "" },
        body: { amount, currency },
      }),
      invalidatesTags: ["contractorFinance"],
    }),
    getHourlyInfo: builder.query({
      query: ({ id, month }) => ({
        url: `contractorroutes/hourly-info/${id}${month ? `?month=${month}` : ""}`,
        method: "GET",
      }),
      providesTags: ["contractorHours"],
    }),
    setClientRate: builder.mutation({
      query: ({ id, clientHourlyRate, clientRateCurrency }) => ({
        url: `contractorroutes/client-rate/${id}`,
        method: "PUT",
        body: { clientHourlyRate, clientRateCurrency },
      }),
      invalidatesTags: ["contractorHours", "contractorFinance"],
    }),
    submitContractorHours: builder.mutation({
      query: (body) => ({
        url: "contractorroutes/hours",
        method: "POST",
        body,
      }),
      invalidatesTags: ["contractorHours", "contractorFinance"],
    }),
    getMyHours: builder.query({
      query: (month) => ({
        url: `contractorroutes/my-hours${month ? `?month=${month}` : ""}`,
        method: "GET",
      }),
      providesTags: ["contractorHours"],
    }),
    setMyEmailPrefs: builder.mutation({
      query: (emailNotifications) => ({
        url: "contractorroutes/my-email-prefs",
        method: "PUT",
        body: { emailNotifications },
      }),
      invalidatesTags: ["contractorroutes"],
    }),
    // rehiring + HR notes
    lookupContractorByEmail: builder.query({
      query: (email) => ({
        url: `contractorroutes/lookup?email=${encodeURIComponent(email || "")}`,
        method: "GET",
      }),
    }),
    reonboardContractor: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `contractorroutes/reonboard/${id}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["contractorroutes", "contractorFinance"],
    }),
    getContractorNotes: builder.query({
      query: (id) => ({
        url: `contractorroutes/notes/${id}`,
        method: "GET",
      }),
      providesTags: (r, e, id) => [{ type: "contractorNotes", id }],
    }),
    addContractorNote: builder.mutation({
      query: ({ id, text }) => ({
        url: `contractorroutes/notes/${id}`,
        method: "POST",
        body: { text },
      }),
      invalidatesTags: (r, e, { id }) => [{ type: "contractorNotes", id }],
    }),
    deleteContractorNote: builder.mutation({
      query: ({ id, noteId }) => ({
        url: `contractorroutes/notes/${id}/${noteId}`,
        method: "DELETE",
      }),
      invalidatesTags: (r, e, { id }) => [{ type: "contractorNotes", id }],
    }),
    contractorList: builder.mutation({
      query: (payload) => ({
        url: "contractorroutes/list",
        method: "POST",
        body: payload,
      }),
      providesTags: ["contractorroutes"],
    }),
    getContractorCounts: builder.mutation({
      query: (payload) => ({
        url: "contractorroutes/counts",
        method: "POST",
        body: payload,
      }),
    }),
    contractorById: builder.query({
      query: (id) => ({
        url: `contractorroutes/byid/${id}`,
        method: "GET",
      }),
      providesTags: ["contractorroutes"],
    }),
    contractorByMail: builder.query({
      query: (email) => ({
        url: `contractorroutes/email/${email}`,
        method: "GET",
      }),
      providesTags: ["contractorroutes"],
    }),
    contractorOnboard: builder.mutation({
      query: (body) => ({
        url: "contractorroutes/onboard",
        method: "POST",
        body,
      }),
      invalidatesTags: ["contractorroutes"],
    }),
    contractorDropdown: builder.query({
      query: () => ({
        url: "contractorroutes/dropdown",
        method: "GET",
      }),
      providesTags: ["contractorroutes"],
    }),
    deleteContractor: builder.mutation({
      query: (id) => ({
        url: `contractorroutes/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["contractorroutes"],
    }),
    sendEmailToContractor: builder.mutation({
      query: (emailData) => ({
        url: "/contractorroutes/send-email",
        method: "POST",
        body: emailData,
      }),
      invalidatesTags: ["contractorroutes"],
    }),
  }),
});

export const {
  useCreateContractorMutation,
  useContractorListMutation,
  useGetContractorFinanceQuery,
  useSetContractorBillingMutation,
  useGetContractorCountsMutation,
  useContractorByIdQuery,
  useContractorByMailQuery,
  useContractorOnboardMutation,
  useContractorDropdownQuery,
  useDeleteContractorMutation,
  useSendEmailToContractorMutation,
  useGetHourlyInfoQuery,
  useSetClientRateMutation,
  useSubmitContractorHoursMutation,
  useGetMyHoursQuery,
  useSetMyEmailPrefsMutation,
  useLazyLookupContractorByEmailQuery,
  useReonboardContractorMutation,
  useGetContractorNotesQuery,
  useAddContractorNoteMutation,
  useDeleteContractorNoteMutation,
} = contractorApi;

//Salary Api

export const SalaryDetailApi = createApi({
  tagTypes: ["salaryDetail"],
  reducerPath: "salaryDetailApi",
  baseQuery: fetchBaseQuery({
    baseUrl: `${baseUrl}/`,
    prepareHeaders: (headers, { getState }) => {
      if (getState()?.authState?.userToken) {
        headers.set("Authorization", getState()?.authState?.userToken);
      }
      return headers;
    },
  }),
  endpoints: (builder) => ({
    createSalaryDetail: builder.mutation({
      query: (payload) => ({
        url: "salary/detail",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["salaryDetail"],
    }),
    getSalaryDetailById: builder.query({
      query: (id) => ({
        url: `salary/detail/${id}`,
        method: "GET",
      }),
      providesTags: ["salaryDetail"],
    }),
    deleteContractorSalary: builder.mutation({
      query: (id) => ({
        url: `salary/detail/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["salaryDetail"],
    }),
  }),
});

export const {
  useCreateSalaryDetailMutation,
  useGetSalaryDetailByIdQuery,
  useDeleteContractorSalaryMutation,
} = SalaryDetailApi;

// TASK API

export const taskApi = createApi({
  reducerPath: "taskApi",
  tagTypes: ["taskroutes"],
  baseQuery: fetchBaseQuery({
    baseUrl: `${baseUrl}/`,
    prepareHeaders: (headers, { getState }) => {
      const token = getState()?.authState?.userToken;
      if (token) {
        headers.set("Authorization", token);
      }
      return headers;
    },
  }),
  endpoints: (builder) => ({
    createTask: builder.mutation({
      query: (payload) => ({
        url: "taskroutes",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["taskroutes"],
    }),
    taskList: builder.mutation({
      query: (payload) => ({
        url: "taskroutes/list",
        method: "POST",
        body: payload,
      }),
      providesTags: ["taskroutes"],
    }),
    gettaskCounts: builder.mutation({
      query: (payload) => ({
        url: "taskroutes/counts",
        method: "POST",
        body: payload,
      }),
    }),
    taskById: builder.query({
      query: (id) => ({
        url: `taskroutes/byid/${id}`,
        method: "GET",
      }),
      providesTags: ["taskroutes"],
    }),
    taskDropdown: builder.query({
      query: () => ({
        url: "taskroutes/dropdown",
        method: "GET",
      }),
      providesTags: ["taskroutes"],
    }),
    deletetask: builder.mutation({
      query: (id) => ({
        url: `taskroutes/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["taskroutes"],
    }),
  }),
});

export const {
  useCreateTaskMutation,
  useTaskListMutation,
  useGetTaskCountsMutation,
  useTaskByIdQuery,
  useTaskDropdownQuery,
  useDeleteTaskMutation,
} = taskApi;

export const invoiceBankApi = createApi({
  reducerPath: "invoiceBankApi",
  tagTypes: ["invoicebankdetails"],
  baseQuery: fetchBaseQuery({
    baseUrl: `${baseUrl}/`,
    prepareHeaders: (headers, { getState }) => {
      const token = getState()?.authState?.userToken;
      if (token) {
        headers.set("Authorization", token);
      }
      return headers;
    },
  }),
  endpoints: (builder) => ({
    addInvoiceBankDetail: builder.mutation({
      query: (data) => ({
        url: "/invoicebankdetails/invoice-bank-details",
        method: "POST",
        body: data,
      }),
    }),
    getInvoiceBankDetails: builder.query({
      query: (userId) => ({
        url: `invoicebankdetails/invoice-bank-details?createdBy=${userId}`,
        method: "GET",
      }),
      providesTags: ["invoicebankdetails"],
    }),
  }),
});

export const {
  useAddInvoiceBankDetailMutation,
  useGetInvoiceBankDetailsQuery,
} = invoiceBankApi;


export const attendanceApi = createApi({
  reducerPath: 'attendanceApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${baseUrl}/`,
    prepareHeaders: (headers, { getState }) => {
      const token = getState().authState.userToken;
      if (token) headers.set('Authorization', token);
      return headers;
    },
  }),
  tagTypes: ['Attendance'],
  endpoints: (builder) => ({
    markAttendance: builder.mutation({
      query: (payload) => ({
        url: '/empattendance/mark',
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: ['Attendance'],
    }),
    getAttendanceByEmail: builder.query({
      query: (email) => `/empattendance?email=${email}`,
      providesTags: ['Attendance'],
    }),
    updateAttendanceByAdminOrStaff: builder.mutation({
      query: ({ employeeId, date, status }) => ({
        url: `/empattendance/update`,
        method: 'PUT',
        body: { employeeId, date, status },
      }),
      invalidatesTags: ['Attendance'],
    }),

    getEmployeeAttendance: builder.query({
      query: () => `/empattendance/self`,
      providesTags: ['Attendance'],
    }),
    getToday: builder.query({
      query: () => '/empattendance/today',
      providesTags: ['Attendance'],
    }),
    clearAttendanceByAdminOrStaff: builder.mutation({
      query: ({ employeeId, date }) => ({
        url: `/empattendance/clear`,
        method: 'POST',
        body: { employeeId, date },
      }),
      invalidatesTags: ['Attendance'],
    }),
    updatePaidLeaveManually: builder.mutation({
      query: ({ employeeId, month, carryForwardPaidLeave }) => ({
        url: `/empattendance/update-paid-leave`,
        method: 'PUT',
        body: { employeeId, month, carryForwardPaidLeave },
      }),
      invalidatesTags: ['Attendance'],
    }),
    clearStaffAttendance: builder.mutation({
      query: ({ date }) => ({
        url: `/empattendance/staff-attendance`,
        method: 'POST',
        body: { date },
      }),
      invalidatesTags: ['Attendance'],
    }),

  }),
});


export const {
  useMarkAttendanceMutation,
  useLazyGetEmployeeAttendanceQuery,
  useLazyGetAttendanceByEmailQuery,
  useGetTodayQuery,
  useUpdateAttendanceByAdminOrStaffMutation,
  useClearAttendanceByAdminOrStaffMutation,
  useUpdatePaidLeaveManuallyMutation,
  useClearStaffAttendanceMutation 
} = attendanceApi;

export const holidayApi = createApi({
  reducerPath: 'holidayApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${baseUrl}/`,
    prepareHeaders: (headers, { getState }) => {
      const token = getState().authState.userToken;
      if (token) headers.set('Authorization', token);
      return headers;
    },
  }),

  endpoints: (builder) => ({
    getHolidays: builder.query({
      query: () => "/holidaymanage/holidays",
      providesTags: ["Holidays"],
    }),
    addHoliday: builder.mutation({
      query: (holiday) => ({
        url: "/holidaymanage/holidays",
        method: "POST",
        body: holiday,
      }),
      invalidatesTags: ["Holidays"],
    }),
    deleteHoliday: builder.mutation({
      query: (id) => ({
        url: `/holidaymanage/holidays/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Holidays"],
    }),
  }),
});

export const {
  useGetHolidaysQuery,
  useAddHolidayMutation,
  useDeleteHolidayMutation,
} = holidayApi;

// Notes api

export const notesApi = createApi({
  reducerPath: "notesApi",
  baseQuery: fetchBaseQuery({
    baseUrl: `${baseUrl}/`,
    prepareHeaders: (headers, { getState }) => {
      const token = getState().authState.userToken;
      if (token) headers.set("Authorization", token);
      return headers;
    },
  }),

  tagTypes: ["Notes"],

  endpoints: (builder) => ({
    getNotesByDetailId: builder.query({
      query: (detailId) => ({
        url: `/notes/${detailId}`,
        method: "GET",
      }),
      providesTags: (result, error, detailId) =>
        result && result.notes
          ? [
            ...result.notes.map(({ _id }) => ({ type: "Notes", id: _id })),
            { type: "Notes", id: detailId },
          ]
          : [{ type: "Notes", id: detailId }],
    }),
    addNotes: builder.mutation({
      query: (note) => ({
        url: "/notes/",
        method: "POST",
        body: note,
      }),
      invalidatesTags: (result, error, { invoiceDetailId }) => [
        { type: "Notes", id: invoiceDetailId },
      ],
    }),
    deleteNote: builder.mutation({
      query: ({ noteId, invoiceDetailId, salaryDetailId }) => ({
        url: `/notes/${noteId}?invoiceDetailId=${invoiceDetailId || ""}&salaryDetailId=${salaryDetailId || ""}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, { invoiceDetailId, salaryDetailId }) => [
        { type: "Notes", id: invoiceDetailId || salaryDetailId },
      ],
    }),
    updateNotes: builder.mutation({
      query: ({ noteId, note }) => ({
        url: `/notes/update/${noteId}`,
        method: "PUT",
        body: note,
      }),
      invalidatesTags: (result, error, { note }) => {
        const id =
          note.invoiceDetailId ||
          note.SalaryDetailId ||
          "NotesList"; // fallback
        return [{ type: "Notes", id }];
      },
    }),
  }),
});

export const {
  useAddNotesMutation,
  useGetNotesByDetailIdQuery,
  useDeleteNoteMutation,
  useUpdateNotesMutation,
  useLazyGetNotesByInvoiceDetailIdQuery,
} = notesApi;


// Leave API
export const leaveApi = createApi({
  reducerPath: 'leaveApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${baseUrl}/leaveRequestRoutes`,
    prepareHeaders: (headers, { getState }) => {
      const token = getState().authState.userToken;
      if (token) headers.set('Authorization', token);
      return headers;
    },
  }),

  tagTypes: ["Leaves"],

  endpoints: (builder) => ({
    getAllLeaveRequests: builder.query({
      query: () => "/getAllLeaveRequests",
      providesTags: ["Leaves"],
    }),

    applyLeave: builder.mutation({
      query: (leaveData) => ({
        url: "/applyLeave",
        method: "POST",
        body: leaveData,
      }),
      invalidatesTags: ["Leaves"],
    }),

    reviewLeave: builder.mutation({
      query: (reviewData) => ({
        url: "/reviewLeave",
        method: "PUT",
        body: reviewData,
      }),
      invalidatesTags: ["Leaves"],
    }),
    getMyLeaveRequests: builder.query({
      query: () => "/my-leave-requests",
      providesTags: ["Leaves"],
    }),
  }),
});

export const {
  useGetAllLeaveRequestsQuery,
  useApplyLeaveMutation,
  useReviewLeaveMutation,
  useGetMyLeaveRequestsQuery
} = leaveApi;


export const attendanceRequestApi = createApi({
  reducerPath: 'attendanceRequestApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${baseUrl}/attendanceRequestRoutes`,
    prepareHeaders: (headers, { getState }) => {
      const token = getState().authState.userToken;
      if (token) headers.set('Authorization', token);
      return headers;
    },
  }),

  tagTypes: ["attendanceRequest"],

  endpoints: (builder) => ({

    getMyAttendanceRequests: builder.query({
      query: () => "/my",
      providesTags: ["attendanceRequest"],
    }),

    applyAttendanceRequest: builder.mutation({
      query: (data) => ({
        url: "/apply",
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["attendanceRequest"],
    }),

    getAllAttendanceRequests: builder.query({
      query: () => `/all`,
      providesTags: ["attendanceRequest"],
    }),

    reviewAttendanceRequest: builder.mutation({
      query: ({ AttendanceRequestId, decision }) => ({
        url: `/review`,
        method: "PUT",
        body: {
          AttendanceRequestId,
          decision,
        },
      }),
      invalidatesTags: ["attendanceRequest"],
    }),

  }),
});



export const {
  useGetMyAttendanceRequestsQuery,
  useGetAllAttendanceRequestsQuery,
  useApplyAttendanceRequestMutation,
  useReviewAttendanceRequestMutation,
} = attendanceRequestApi;

// Requests API — reimbursements + profile change requests
export const marketingApi = createApi({
  reducerPath: "marketingApi",
  tagTypes: ["MkStats", "MkTemplate", "MkContact", "MkCampaign", "MkAsset", "MkSender", "MkAccess", "MkList", "MkSeq"],
  baseQuery: fetchBaseQuery({
    baseUrl: `${baseUrl}/marketing`,
    prepareHeaders: (headers, { getState }) => {
      const token = getState()?.authState?.userToken;
      if (token) headers.set("Authorization", token);
      return headers;
    },
  }),
  endpoints: (builder) => ({
    getMarketingStats: builder.query({
      query: () => "/stats",
      providesTags: ["MkStats"],
    }),
    purgeOptouts: builder.mutation({
      query: () => ({ url: "/contacts/optouts", method: "DELETE" }),
      invalidatesTags: ["MkContact", "MkList", "MkStats"],
    }),
    getSendAdvice: builder.query({
      query: (params) => ({ url: "/send-advice", params }),
      providesTags: ["MkStats"],
    }),
    getContactHistory: builder.query({
      query: (id) => `/contacts/${id}/history`,
      providesTags: ["MkContact"],
    }),
    getSequences: builder.query({
      query: () => "/sequences",
      providesTags: ["MkSeq"],
    }),
    getSequenceDetail: builder.query({
      query: (id) => `/sequences/${id}`,
      providesTags: ["MkSeq"],
    }),
    saveSequence: builder.mutation({
      query: (body) => ({ url: "/sequences", method: "POST", body }),
      invalidatesTags: ["MkSeq"],
    }),
    setSequenceStatus: builder.mutation({
      query: ({ id, action }) => ({ url: `/sequences/${id}/status`, method: "PUT", body: { action } }),
      invalidatesTags: ["MkSeq", "MkCampaign", "MkStats"],
    }),
    deleteSequence: builder.mutation({
      query: (id) => ({ url: `/sequences/${id}`, method: "DELETE" }),
      invalidatesTags: ["MkSeq"],
    }),
    getCampaigns: builder.query({
      query: () => "/campaigns",
      providesTags: ["MkCampaign"],
    }),
    getCampaignDetail: builder.query({
      query: (id) => `/campaigns/${id}`,
    }),
    sendCampaign: builder.mutation({
      query: (body) => ({ url: "/send", method: "POST", body }),
      invalidatesTags: ["MkStats", "MkCampaign", "MkContact"],
    }),
    sendTestEmail: builder.mutation({
      query: (body) => ({ url: "/send-test", method: "POST", body }),
      invalidatesTags: ["MkStats"],
    }),
    getEmailTemplates: builder.query({
      query: () => "/templates",
      providesTags: ["MkTemplate"],
    }),
    saveEmailTemplate: builder.mutation({
      query: (body) => ({ url: "/templates", method: "POST", body }),
      invalidatesTags: ["MkTemplate"],
    }),
    deleteEmailTemplate: builder.mutation({
      query: (id) => ({ url: `/templates/${id}`, method: "DELETE" }),
      invalidatesTags: ["MkTemplate"],
    }),
    getMarketingContacts: builder.query({
      query: (params) => ({ url: "/contacts", params }),
      providesTags: ["MkContact"],
    }),
    addMarketingContact: builder.mutation({
      query: (body) => ({ url: "/contacts", method: "POST", body }),
      invalidatesTags: ["MkContact", "MkStats", "MkList"],
    }),
    updateMarketingContact: builder.mutation({
      query: (body) => ({ url: "/contacts", method: "PUT", body }),
      invalidatesTags: ["MkContact", "MkStats"],
    }),
    deleteMarketingContact: builder.mutation({
      query: (id) => ({ url: `/contacts/${id}`, method: "DELETE" }),
      invalidatesTags: ["MkContact", "MkStats"],
    }),
    markContactReplied: builder.mutation({
      query: ({ id, replied }) => ({ url: `/contacts/${id}/replied`, method: "PUT", body: { replied } }),
      invalidatesTags: ["MkContact", "MkStats"],
    }),
    importLeadFile: builder.mutation({
      query: (body) => ({ url: "/contacts/import-leads", method: "POST", body }),
      invalidatesTags: ["MkContact", "MkStats", "MkList"],
    }),
    importClients: builder.mutation({
      query: (body) => ({ url: "/contacts/import-clients", method: "POST", body: body || {} }),
      invalidatesTags: ["MkContact", "MkStats", "MkList"],
    }),
    getLeadFiles: builder.query({
      query: () => "/lead-files",
    }),
    getContactLists: builder.query({
      query: () => "/lists",
      providesTags: ["MkList"],
    }),
    saveContactList: builder.mutation({
      query: (body) => ({ url: "/lists", method: "POST", body }),
      invalidatesTags: ["MkList"],
    }),
    deleteContactList: builder.mutation({
      query: (id) => ({ url: `/lists/${id}`, method: "DELETE" }),
      invalidatesTags: ["MkList", "MkContact"],
    }),
    updateListMembership: builder.mutation({
      query: (body) => ({ url: "/lists/membership", method: "PUT", body }),
      invalidatesTags: ["MkList", "MkContact"],
    }),
    getAssets: builder.query({
      query: () => "/assets",
      providesTags: ["MkAsset"],
    }),
    saveAsset: builder.mutation({
      query: (body) => ({ url: "/assets", method: "POST", body }),
      invalidatesTags: ["MkAsset"],
    }),
    deleteAsset: builder.mutation({
      query: (id) => ({ url: `/assets/${id}`, method: "DELETE" }),
      invalidatesTags: ["MkAsset"],
    }),
    getSenders: builder.query({
      query: () => "/senders",
      providesTags: ["MkSender"],
    }),
    saveSender: builder.mutation({
      query: (body) => ({ url: "/senders", method: "POST", body }),
      invalidatesTags: ["MkSender"],
    }),
    deleteSender: builder.mutation({
      query: (id) => ({ url: `/senders/${id}`, method: "DELETE" }),
      invalidatesTags: ["MkSender"],
    }),
    getMarketingAccess: builder.query({
      query: () => "/access",
      providesTags: ["MkAccess"],
    }),
    setMarketingAccess: builder.mutation({
      query: (body) => ({ url: "/access", method: "PUT", body }),
      invalidatesTags: ["MkAccess"],
    }),
  }),
});

export const {
  useGetMarketingStatsQuery,
  useGetCampaignsQuery,
  useGetSendAdviceQuery,
  usePurgeOptoutsMutation,
  useGetContactHistoryQuery,
  useGetSequencesQuery,
  useGetSequenceDetailQuery,
  useSaveSequenceMutation,
  useSetSequenceStatusMutation,
  useDeleteSequenceMutation,
  useGetCampaignDetailQuery,
  useSendCampaignMutation,
  useSendTestEmailMutation,
  useGetEmailTemplatesQuery,
  useSaveEmailTemplateMutation,
  useDeleteEmailTemplateMutation,
  useGetMarketingContactsQuery,
  useAddMarketingContactMutation,
  useUpdateMarketingContactMutation,
  useMarkContactRepliedMutation,
  useDeleteMarketingContactMutation,
  useImportLeadFileMutation,
  useImportClientsMutation,
  useGetLeadFilesQuery,
  useGetContactListsQuery,
  useSaveContactListMutation,
  useDeleteContactListMutation,
  useUpdateListMembershipMutation,
  useGetAssetsQuery,
  useSaveAssetMutation,
  useDeleteAssetMutation,
  useGetSendersQuery,
  useSaveSenderMutation,
  useDeleteSenderMutation,
  useGetMarketingAccessQuery,
  useSetMarketingAccessMutation,
} = marketingApi;

export const announcementsApi = createApi({
  reducerPath: "announcementsApi",
  tagTypes: ["Announcement"],
  baseQuery: fetchBaseQuery({
    baseUrl: `${baseUrl}/announcements`,
    prepareHeaders: (headers, { getState }) => {
      const token = getState()?.authState?.userToken;
      if (token) headers.set("Authorization", token);
      return headers;
    },
  }),
  endpoints: (builder) => ({
    getPendingAnnouncements: builder.query({
      query: () => "/pending",
      providesTags: ["Announcement"],
    }),
    acknowledgeAnnouncement: builder.mutation({
      query: (body) => ({ url: "/ack", method: "POST", body }),
      invalidatesTags: ["Announcement"],
    }),
  }),
});

export const {
  useGetPendingAnnouncementsQuery,
  useAcknowledgeAnnouncementMutation,
} = announcementsApi;

export const thoughtsApi = createApi({
  reducerPath: "thoughtsApi",
  tagTypes: ["Thought"],
  baseQuery: fetchBaseQuery({
    baseUrl: `${baseUrl}/thoughts`,
    prepareHeaders: (headers, { getState }) => {
      const token = getState()?.authState?.userToken;
      if (token) headers.set("Authorization", token);
      return headers;
    },
  }),
  endpoints: (builder) => ({
    getTodayThought: builder.query({
      query: () => "/today",
      providesTags: ["Thought"],
    }),
    getThoughts: builder.query({
      query: () => "/",
      providesTags: ["Thought"],
    }),
    createThought: builder.mutation({
      query: (body) => ({ url: "/", method: "POST", body }),
      invalidatesTags: ["Thought"],
    }),
    updateThought: builder.mutation({
      query: (body) => ({ url: "/", method: "PUT", body }),
      invalidatesTags: ["Thought"],
    }),
    deleteThought: builder.mutation({
      query: (id) => ({ url: `/${id}`, method: "DELETE" }),
      invalidatesTags: ["Thought"],
    }),
  }),
});

export const {
  useGetTodayThoughtQuery,
  useGetThoughtsQuery,
  useCreateThoughtMutation,
  useUpdateThoughtMutation,
  useDeleteThoughtMutation,
} = thoughtsApi;

export const requestsApi = createApi({
  reducerPath: "requestsApi",
  baseQuery: fetchBaseQuery({
    baseUrl: `${baseUrl}/requests`,
    prepareHeaders: (headers, { getState }) => {
      const token = getState().authState.userToken;
      if (token) headers.set("Authorization", token);
      return headers;
    },
  }),
  tagTypes: ["Reimbursement", "ProfileChange", "Ticket"],
  endpoints: (builder) => ({
    createReimbursement: builder.mutation({
      query: (body) => ({ url: "/reimbursement", method: "POST", body }),
      invalidatesTags: ["Reimbursement"],
    }),
    getMyReimbursements: builder.query({
      query: () => "/reimbursement/my",
      providesTags: ["Reimbursement"],
    }),
    getAllReimbursements: builder.query({
      query: () => "/reimbursement/all",
      providesTags: ["Reimbursement"],
    }),
    reviewReimbursement: builder.mutation({
      query: (body) => ({ url: "/reimbursement/review", method: "PUT", body }),
      invalidatesTags: ["Reimbursement"],
    }),
    createProfileChange: builder.mutation({
      query: (body) => ({ url: "/profile-change", method: "POST", body }),
      invalidatesTags: ["ProfileChange"],
    }),
    getMyProfileChanges: builder.query({
      query: () => "/profile-change/my",
      providesTags: ["ProfileChange"],
    }),
    getAllProfileChanges: builder.query({
      query: () => "/profile-change/all",
      providesTags: ["ProfileChange"],
    }),
    reviewProfileChange: builder.mutation({
      query: (body) => ({ url: "/profile-change/review", method: "PUT", body }),
      invalidatesTags: ["ProfileChange"],
    }),
    createTicket: builder.mutation({
      query: (body) => ({ url: "/ticket", method: "POST", body }),
      invalidatesTags: ["Ticket"],
    }),
    getMyTickets: builder.query({
      query: () => "/ticket/my",
      providesTags: ["Ticket"],
    }),
    getAllTickets: builder.query({
      query: () => "/ticket/all",
      providesTags: ["Ticket"],
    }),
    updateTicketStatus: builder.mutation({
      query: (body) => ({ url: "/ticket/status", method: "PUT", body }),
      invalidatesTags: ["Ticket"],
    }),
    replyToTicket: builder.mutation({
      query: (body) => ({ url: "/ticket/reply", method: "POST", body }),
      invalidatesTags: ["Ticket"],
    }),
    resetUserPassword: builder.mutation({
      query: (body) => ({ url: "/reset-password", method: "PUT", body }),
    }),
  }),
});

export const {
  useCreateReimbursementMutation,
  useGetMyReimbursementsQuery,
  useGetAllReimbursementsQuery,
  useReviewReimbursementMutation,
  useCreateProfileChangeMutation,
  useGetMyProfileChangesQuery,
  useGetAllProfileChangesQuery,
  useReviewProfileChangeMutation,
  useCreateTicketMutation,
  useGetMyTicketsQuery,
  useGetAllTicketsQuery,
  useUpdateTicketStatusMutation,
  useReplyToTicketMutation,
  useResetUserPasswordMutation,
} = requestsApi;
